import { Prop, Vue, Component, Watch } from 'vue-property-decorator';
import { CreateElement, VNode } from 'vue';

@Component
export default class ClClickAwayListener extends Vue {
  /**
   * If true, the click away listener will be disabled.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disabled!: boolean;

  /**
   * A function used to test whether or not the component should emit the click-away event.
   */
  @Prop(Function)
  public shouldEmitClickAway!: (path: Element[], event: MouseEvent) => boolean;

  defaultShouldEmitClickAway(path: Element[], event: MouseEvent): boolean {
    return path
      ? path.indexOf(this.$el) < 0
      : !this.$el.contains(event.target as Node);
  }

  handleClickAway(event: MouseEvent) {
    if (event.defaultPrevented) {
      return;
    }
    const path =
      (event as any).path ||
      (event.composedPath ? event.composedPath() : undefined);

    const isValid = this.shouldEmitClickAway
      ? this.shouldEmitClickAway(path, event)
      : this.defaultShouldEmitClickAway(path, event);

    if (!isValid) {
      return;
    }

    this.$emit('click-away', path, event);
  }

  mounted() {
    if (this.disabled) {
      return;
    }
    this.attachListener();
  }

  beforeDestroy() {
    if (this.disabled) {
      return;
    }
    this.detachListener();
  }

  attachListener() {
    if (document.documentElement) {
      document.documentElement.addEventListener('click', this.handleClickAway);
    }
  }

  detachListener() {
    if (document.documentElement) {
      document.documentElement.removeEventListener(
        'click',
        this.handleClickAway
      );
    }
  }

  @Watch('disabled')
  handleDisabled(newVal: boolean, oldVal: boolean) {
    if (oldVal === true && newVal === false) {
      this.attachListener();
    } else if (oldVal === false && newVal === true) {
      this.detachListener();
    }
  }

  render(h: CreateElement): VNode {
    return h('span', this.$slots.default);
  }
}
